import { arrayOf, bool, func, shape, string } from "prop-types";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router-dom";
import { goBack, push } from "react-router-redux";
import classNames from "classnames";
import * as configUtils from "util/config";

import { DebinScheduledDetail, Box, Spinner, Text, Tag, Hint, ModalWarn } from "@ui-kit/components/index.es";

import { actions as debinActions, selectors as debinSelectors } from "reducers/debin";

import { actions as notificationActions } from "reducers/notification";
import * as i18n from "util/i18n";
import { getIncomingDebinAccountName } from "util/accounts";

import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import Button from "pages/_components/Button";
import { resizableRoute } from "pages/_components/Resizable";
import { applyEmbeddedV3ShellLayoutState, clearEmbeddedV3ShellLayoutState } from "util/v3ShellLayoutSync";

function DebinScheduledDetailPage({
    isDesktop,
    dispatch,
    programmedRecurrencies,
    seller,
    accounts,
    match,
    loading,
    embeddedInV3Shell = false,
}) {
    useEffect(() => {
        if (!accounts.length) {
            dispatch(debinActions.debinPreActivity());
        }
    }, [dispatch, accounts.length]);

    const debinDetail = programmedRecurrencies.find((object) => object.id === parseInt(match.params.id, 10));

    useEffect(() => {
        if (debinDetail) {
            dispatch(debinActions.debinSeller(debinDetail.vendedor.cuit));
        }
    }, [debinDetail, dispatch]);

    useEffect(() => {
        if (!embeddedInV3Shell) {
            return undefined;
        }
        if (debinDetail) {
            applyEmbeddedV3ShellLayoutState(dispatch, {
                embeddedInV3Shell,
                isDesktop,
                scopes: ["debinDetail", "debin"],
                titleKey: "debin.programmed.detail.title.mobile",
                goBackPath: "/v2/debin/scheduled",
            });
        }
        return () => {
            clearEmbeddedV3ShellLayoutState(dispatch);
        };
    }, [dispatch, embeddedInV3Shell, isDesktop, debinDetail, match.params.id]);

    const [isModalWarnVisible, setIsModalWarnVisible] = useState(false);
    const [selectedAction, setSelectedAction] = useState(undefined);
    const [submitting, setSubmitting] = useState(false);

    const handleBack = () => {
        dispatch(goBack());
    };

    function getAccountDescriptionByCBU(cbu) {
        const account = accounts.find((obj) => obj.cbu === cbu);

        return getIncomingDebinAccountName(account);
    }

    const mappedDebin = debinDetail
        ? {
              ...debinDetail,
              autorizated: debinDetail.autorizado,
              buyer: {
                  cbu: debinDetail.comprador.cbu,
                  cuit: debinDetail.comprador.cuit,
                  name: debinDetail.comprador.nombre,
              },
              confirmation_date: debinDetail.fecha_confirmacion,
              creation_date: debinDetail.fecha_creacion,
              debin: {
                  amount: debinDetail.debin.importe,
                  concept: debinDetail.debin.concepto,
                  currency: debinDetail.debin.moneda,
                  detail: debinDetail.debin.detalle,
                  limit_part: debinDetail.debin.limite_cuotas,
                  reference: debinDetail.debin.referencia,
                  service: debinDetail.debin.prestacion,
              },
              seller: {
                  cuit: debinDetail.vendedor.cuit,
                  name: seller?.titular,
              },
              status: debinDetail.estado,
              debitAccount: getAccountDescriptionByCBU(debinDetail.comprador.cbu),
          }
        : null;

    const partsList = debinDetail
        ? Array.from({ length: debinDetail.debin.limite_cuotas }, (_, i) => ({
              number: i + 1,
              currency: debinDetail.debin.moneda === "032" ? "ARS" : "USD",
              amount: debinDetail.debin.importe,
          }))
        : [];
    const getCardLabel = (id) => i18n.get(`debin.programmed.label.card.${id}`);
    const getTableLabel = (id) => i18n.get(`debin.programmed.label.table.${id}`);

    const getEstado = (recurrency) => {
        if (recurrency.estado === "ACTIVA" && recurrency.autorizated === "AUTORIZADO") {
            return "ACTIVA";
        }
        if (recurrency.estado === "INACTIVA" && recurrency.autorizated === "AUTORIZADO") {
            return "INACTIVA";
        }
        if (
            (recurrency.estado === "ACTIVA" || recurrency.estado === "INACTIVA") &&
            recurrency.autorizated === "PENDIENTE"
        ) {
            return "PENDIENTE";
        }
        if (
            (recurrency.estado === "ACTIVA" || recurrency.estado === "INACTIVA") &&
            recurrency.autorizated === "RECHAZADO"
        ) {
            return "RECHAZADO";
        }
        return recurrency.estado;
    };
    const getStatusToDisplay = (recurrency) => {
        if (recurrency.estado === "ACTIVA" && recurrency.autorizado === "AUTORIZADO") {
            return "ACTIVA";
        }
        if (recurrency.estado === "INACTIVA" && recurrency.autorizado === "AUTORIZADO") {
            return "INACTIVA";
        }
        if (
            (recurrency.estado === "ACTIVA" || recurrency.estado === "INACTIVA") &&
            recurrency.autorizado === "PENDIENTE"
        ) {
            return "PENDIENTE";
        }
        if (
            (recurrency.estado === "ACTIVA" || recurrency.estado === "INACTIVA") &&
            recurrency.autorizado === "RECHAZADO"
        ) {
            return "RECHAZADO";
        }
        return recurrency.estado;
    };

    const handleConfirmAction = (action) => {
        const actionMap = {
            enable: "activar",
            disable: "desactivar",
            authorize: "autorizar",
            decline: "rechazar",
        };
        dispatch(debinActions.setRecurrencyDebinAction({ action: actionMap[action], debin: debinDetail }));
        dispatch(debinActions.debinSeller(debinDetail?.vendedor?.cuit));
        dispatch(push("/v2/debin/scheduled/confirmation"));
    };

    const handleDebinAction = (action) => {
        if (debinDetail.debin.concepto === "PRE" && (action === "enable" || action === "authorize")) {
            dispatch(
                notificationActions.showNotification(
                    i18n.get(`debin.programmed.validation.concept.${action}.warning`),
                    "warning",
                    ["debinDetail"],
                ),
            );
        } else if (action === "enable" || action === "authorize") {
            setSelectedAction(action);
            setIsModalWarnVisible(true);
        } else {
            handleConfirmAction(action);
        }
    };

    const handleDebinActionWithPending = (action) => {
        const actionMap = {
            enable: "activar",
            disable: "desactivar",
            authorize: "autorizar",
            decline: "rechazar",
        };

        const spanishAction = actionMap[action]?.toUpperCase();
        const transaccion = debinDetail.transactions?.find((tx) => tx.action === spanishAction);

        if (transaccion) {
            setSubmitting(true);
            dispatch(debinActions.acceptIncomingRequestPrePending(transaccion?.idTransaction, true));
            return;
        }

        handleDebinAction(action);
    };

    const programedDebinMvp2Enabled = configUtils.getBoolean("frontend.show.programedDebin.mvp2.enable", false);

    const getAvailableActions = (debin) => {
        const status = getEstado(debin);
        const actions = [];

        if (status === "ACTIVA" && programedDebinMvp2Enabled) {
            actions.push({ label: "debin.programmed.label.contextMenu.disable", action: "disable" });
        } else if (status === "INACTIVA" && programedDebinMvp2Enabled) {
            actions.push({ label: "debin.programmed.label.contextMenu.enable", action: "enable" });
        } else if (status === "PENDIENTE") {
            actions.push({ label: "debin.programmed.label.contextMenu.authorize", action: "authorize" });
            actions.push({ label: "debin.programmed.label.contextMenu.decline", action: "decline" });
        } else if (status === "RECHAZADO") {
            actions.push({ label: "debin.programmed.label.contextMenu.authorize", action: "authorize" });
        }
        return actions;
    };

    const rightContent = () => {
        const actions = getAvailableActions(mappedDebin);
        return (
            <Box flex gap={12}>
                {actions.map((item) => (
                    <Button
                        key={item.action}
                        className="btn-primary"
                        block
                        onClick={() => handleDebinActionWithPending(item.action)}
                        label={item.label}
                    />
                ))}
            </Box>
        );
    };

    if (loading || submitting || !mappedDebin) {
        return (
            <Box flex justify="center" align="center" column py={64}>
                <Spinner size="large" />
            </Box>
        );
    }

    const status = getStatusToDisplay(debinDetail);

    const tagVariants = {
        INACTIVA: "neutral",
        ACTIVA: "success",
        PENDIENTE: "warning",
        RECHAZADO: "error",
    };

    const headText = isDesktop ? (
        `${i18n.get("debin.programmed.detail.id")} ${debinDetail.id}`
    ) : (
        <Box column align="left" justify="left">
            <Box flex>
                <Text className="my-0">{debinDetail.debin.concepto}</Text>
                <Box ml={24} mx={2}>
                    <Tag variant={tagVariants[status]} className="p-025 ml-2">
                        <Text variant="caption">{status}</Text>
                    </Tag>
                </Box>
            </Box>
            <Text>{`${i18n.get("debin.programmed.detail.id")} ${debinDetail.id}`}</Text>
        </Box>
    );

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="debinDetail" />}
            <Box css={{ backgroundColor: "#f9f9f9", minHeight: "100%" }}>
                <Box mx={isDesktop ? 40 : null} my={isDesktop ? 24 : null} className="admin-detail-head px-0">
                    {!embeddedInV3Shell && !isDesktop && (
                        <Head
                            title="debin.programmed.detail.title.mobile"
                            titleClassName="my-0 align-center"
                            onBack={!isDesktop && handleBack}
                            headerClassName={classNames({ "blue-main-header-mobile": !isDesktop })}
                            messageTooltip="debin.programmed.hint"
                            hintText="debin.programmed.hint"
                            alwaysShowMessage={isDesktop}
                        />
                    )}
                    {(!embeddedInV3Shell || isDesktop) && isDesktop && <Head onBack={handleBack} />}
                    {(!embeddedInV3Shell || isDesktop) && isDesktop && (
                        <Head
                            centerElement={() => (
                                <Box flex align="center" justify="center" gap={8}>
                                    <Text className="my-0">{debinDetail.debin.concepto}</Text>
                                    <Box ml={24} mx={2}>
                                        <Tag variant={tagVariants[status]} className="p-025 ml-2">
                                            <Text variant="caption">{status}</Text>
                                        </Tag>
                                    </Box>
                                    {status !== "INACTIVA DEFINITIVA" && (
                                        <Hint
                                            text={i18n.get(`debin.programmed.detail.hint.${status.toLowerCase()}`)}
                                            size="big"
                                        />
                                    )}
                                </Box>
                            )}
                            onBack={!isDesktop && handleBack}
                            headerClassName={classNames({ "blue-main-header-mobile": !isDesktop })}
                            rightContent={rightContent}
                        />
                    )}
                    <Box>
                        <DebinScheduledDetail
                            headText={headText}
                            debin={mappedDebin}
                            partsList={partsList}
                            getCardLabel={getCardLabel}
                            getTableLabel={getTableLabel}
                            loading={loading || submitting}
                        />
                    </Box>
                </Box>
                {!isDesktop && (
                    <Box column p={24} gap={12}>
                        {getAvailableActions(mappedDebin).map((item) => (
                            <Button
                                key={item.action}
                                className="btn-primary"
                                block
                                onClick={() => handleDebinActionWithPending(item.action)}
                                label={item.label}
                            />
                        ))}
                    </Box>
                )}
            </Box>
            <ModalWarn
                isVisible={isModalWarnVisible}
                titleText={i18n.get("debin.programmed.warning.modal.titleText")}
                descriptionText={i18n.get("debin.programmed.warning.modal.descriptionText")}
                handleConfirm={() => handleConfirmAction(selectedAction)}
                handleCancel={() => setIsModalWarnVisible(false)}
            />
        </>
    );
}

DebinScheduledDetailPage.propTypes = {
    dispatch: func.isRequired,
    programmedRecurrencies: arrayOf(shape({})).isRequired,
    seller: shape({}),
    accounts: arrayOf(shape({})).isRequired,
    loading: bool.isRequired,
    match: shape({
        params: shape({
            id: string,
        }),
    }).isRequired,
    isDesktop: bool.isRequired,
    embeddedInV3Shell: bool,
};

DebinScheduledDetailPage.defaultProps = {
    seller: null,
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    programmedRecurrencies: debinSelectors.getProgrammedRecurrencies(state),
    seller: debinSelectors.getSeller(state),
    accounts: debinSelectors.getAccounts(state),
    loading: debinSelectors.getFetching(state),
});

export default withRouter(compose(connect(mapStateToProps), resizableRoute)(DebinScheduledDetailPage));
